/*
 * HTTP common Base Instance
 */
import Vue from 'vue';
import axios from 'axios';
import jwt from 'jsonwebtoken';
import store from '@/store';
import trackRequest from '@/plugins/MyTracker/directives/trackerRequest';
import EventBus from '@/eventBus';

export const HTTP = axios.create({
  baseURL: process.env.API_URL || window.apiURL,
  timeout: 300000,
  headers: {},
});

const AVAILABLE_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];
const UNAVAILABLE_SERVICES = ['MARKETDATA', 'INVESTORRELATIONFIRMS', 'EVENTS', 'INSIGHTS', 'DATA', 'CONTACTS', 'SHAREHOLDERS', 'MARKETMAKERS', 'CAMPAIGNS'];
let TRACK_EVENT_NAME = false;
let IS_IGNORE_TRACK_REQUEST = false;

/**
 *
 * @param {String} method HTTP method GET, POST, PUT, PATCH, DELETE
 * @param {String} url
 * @param {Object} params
 * @param {String} objResponse
 */
export const api = async (method, url, params, objResponse = 'data') => {
  try {
    // eslint-disable-next-line
    HTTP.defaults.headers.common['Authorization'] = `Bearer ${store ? store.getters['auth/access_token'] : ''}`;
    if (store.getters['company/company_id'] === '') {
      const urlParam = url.split('/')[0] !== '' ? url.split('/')[0] : url.split('/')[1];
      if (UNAVAILABLE_SERVICES.includes(urlParam.toUpperCase())) return [];
    }
    const checkMethod = AVAILABLE_METHODS
      .map((a) => a.toLowerCase())
      .filter((a) => a === method).length > 0;
    if (!checkMethod) throw new Error('Method param is invalid.');
    if (!url) throw new Error('Url param is required.');

    const response = await HTTP[method](url, params);
    let data = {};
    if (objResponse === 'data') data = response.data.data;
    else data = objResponse === 'status' ? response : response.data;
    return data;
  } catch (e) {
    Vue.config.errorHandler(e);
    throw e;
  }
};

const getRequestInfo = (data) => {
  let endpoint = (data.config.url).replace(`${data.config.baseURL}/`, '');
  const index = endpoint.indexOf('?');
  endpoint = index >= 0 ? endpoint.substring(0, index) : endpoint;
  const method = data.config.method;
  return [endpoint, method];
};

HTTP.interceptors.request.use(async (config) => {
  IS_IGNORE_TRACK_REQUEST = false;
  if (config.params) {
    if (config.params.trackrequest && config.params.tracker_event_name) {
      TRACK_EVENT_NAME = config.params.tracker_event_name;
    }
    IS_IGNORE_TRACK_REQUEST = !!config.params.isIgnoreTrackRequest;
    // eslint-disable-next-line
    delete config.params.trackrequest;
    // eslint-disable-next-line
    delete config.params.tracker_event_name;
    // eslint-disable-next-line
    delete config.params.isIgnoreTrackRequest;
  } else if (config.data) {
    IS_IGNORE_TRACK_REQUEST = !!config.data.isIgnoreTrackRequest;
    // eslint-disable-next-line
    delete config.data.isIgnoreTrackRequest;
  }

  if (process.env.NODE_ENV === 'testing') return config;
  const auth = JSON.parse(localStorage.getItem('vuex')).auth;
  if (auth.access_token) {
    const decoded = jwt.decode(auth.access_token) || {};
    const current = new Date();
    const expiration = new Date(decoded.exp * 1000);
    const isExpired = current.getTime() >= expiration.getTime();

    const decodedRefreshToken = jwt.decode(auth.refresh_token);
    const expRefreshToken = new Date(decodedRefreshToken.exp * 1000);
    const isRefreshTokenExpired = current.getTime() >= expRefreshToken.getTime();

    if (isRefreshTokenExpired) {
      Vue.config.errorHandler({ response: { status: 401 } });
      return {
        ...config,
      };
    }

    if (isExpired && config.url !== 'auth/token') {
      await store.dispatch('auth/refreshToken', auth);
      const auth2 = JSON.parse(localStorage.getItem('vuex')).auth;
      EventBus.$emit('setToken', auth2.access_token);
      // eslint-disable-next-line
      config.headers.Authorization = `Bearer ${auth2.access_token}`;
    }
  }
  return config;
}, (error) => Promise.reject(error));

HTTP.interceptors.response.use(async (response) => {
  if (response && response.config) {
    const [endpoint, method] = [...getRequestInfo(response)];
    trackRequest.emited({
      response,
      endpoint,
      method,
      event_name: TRACK_EVENT_NAME,
      isIgnoreTrackRequest: IS_IGNORE_TRACK_REQUEST,
    });
  }
  TRACK_EVENT_NAME = false;
  return response;
}, async (error) => {
  if (error && error.config) {
    const [endpoint, method] = [...getRequestInfo(error)];
    trackRequest.emited({
      response: error.response,
      endpoint,
      method,
      event_name: TRACK_EVENT_NAME,
      isIgnoreTrackRequest: IS_IGNORE_TRACK_REQUEST,
    });
  }

  if (error.response && error.response.status === 503) {
    store.commit('auth/update', { maintenance_mode: true });
    if (window.location.pathname !== '/maintenance') window.location.href = '/maintenance';
  } else store.commit('auth/update', { maintenance_mode: false });
  TRACK_EVENT_NAME = false;
  return Promise.reject(error);
});
